import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, AlertOctagon, ArrowUpRight } from "lucide-react";
import ShowcaseVideo from "@/components/ShowcaseVideo";
import Reveal from "@/components/Reveal";
import { SECTOR_JOURNEYS, SECTOR_SECTION } from "@/data/sectorJourneys";

const IDS = SECTOR_JOURNEYS.map((s) => s.id);

function initialId() {
  if (typeof window === "undefined") return IDS[0];
  const h = window.location.hash.replace("#", "");
  return IDS.includes(h) ? h : IDS[0];
}

export default function SectorJourneySelector({ index = SECTOR_SECTION.index }) {
  const [activeId, setActiveId] = useState(initialId);
  const tabRefs = useRef({});
  const active = SECTOR_JOURNEYS.find((s) => s.id === activeId) || SECTOR_JOURNEYS[0];

  const select = useCallback((id) => {
    setActiveId(id);
    if (typeof window !== "undefined") window.history.replaceState(null, "", `#${id}`);
  }, []);

  // Deep-link: landing on #<sector> (or navigating the hash) selects that proof and
  // scrolls the Operational Proofs section into view. Tab clicks use replaceState (no
  // hashchange), so switching tabs never triggers this scroll.
  const scrollToSection = useCallback(() => {
    const el = document.getElementById("sector-journeys");
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -40 });
    else el.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    const h = window.location.hash.replace("#", "");
    if (!IDS.includes(h)) return;
    // Wait for Lenis + layout before scrolling on first load.
    const t = setTimeout(scrollToSection, 500);
    return () => clearTimeout(t);
  }, [scrollToSection]);

  useEffect(() => {
    const onHash = () => {
      const h = window.location.hash.replace("#", "");
      if (IDS.includes(h)) {
        setActiveId(h);
        scrollToSection();
      }
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [scrollToSection]);

  const onTabKeyDown = (e) => {
    const idx = IDS.indexOf(active.id);
    let next = null;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = IDS[(idx + 1) % IDS.length];
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = IDS[(idx - 1 + IDS.length) % IDS.length];
    else if (e.key === "Home") next = IDS[0];
    else if (e.key === "End") next = IDS[IDS.length - 1];
    if (next) {
      e.preventDefault();
      select(next);
      tabRefs.current[next]?.focus();
    }
  };

  // Keyed remount pauses the previous video, swaps poster/source/transcript cleanly.
  const item = {
    id: active.id,
    kind: "featured",
    title: active.title,
    caption: active.shortDescription,
    videoUrl: active.videoUrl,
    posterUrl: active.posterUrl,
    captionsUrl: active.captionsUrl,
    destinationUrl: active.destinationUrl,
    mediaStatus: active.mediaStatus,
    deploymentStatus: active.deploymentStatus,
    sector: active.label,
    transcript: active.transcript,
    outcomes: active.outcomes,
  };

  return (
    <section
      id="sector-journeys"
      className="border-y border-ink/12 bg-stone py-24 md:py-36"
      data-testid="sector-journey-selector"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        {/* Header */}
        <div className="mb-8 flex items-center gap-4">
          <span className="font-mono text-xs tracking-[0.24em] text-teal">{index}</span>
          <span className="h-px w-10 bg-ink/25" />
          <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-ink/45">
            {SECTOR_SECTION.eyebrow}
          </span>
        </div>
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <h2 className="font-serif text-2xl leading-[1.15] tracking-tight text-ink sm:text-3xl lg:col-span-7 lg:text-[2.5rem]">
            {SECTOR_SECTION.heading}
          </h2>
          <Reveal delay={0.15} className="lg:col-span-5 lg:pt-2">
            <p className="font-sans text-base leading-relaxed text-ink/70 md:text-lg">
              {SECTOR_SECTION.supporting}
            </p>
          </Reveal>
        </div>

        {/* Sector tabs */}
        <div
          role="tablist"
          aria-label="Sector journeys"
          onKeyDown={onTabKeyDown}
          className="mt-12 flex flex-wrap gap-3"
        >
          {SECTOR_JOURNEYS.map((s) => {
            const isActive = s.id === active.id;
            return (
              <button
                key={s.id}
                ref={(el) => (tabRefs.current[s.id] = el)}
                role="tab"
                id={`sector-tab-${s.id}`}
                aria-selected={isActive}
                aria-controls="sector-stage"
                tabIndex={isActive ? 0 : -1}
                data-testid={`sector-tab-${s.id}`}
                onClick={() => select(s.id)}
                className={`min-w-[7.5rem] border px-6 py-3 font-sans text-sm transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal ${
                  isActive
                    ? "border-ink bg-ink text-stone"
                    : "border-ink/25 text-ink hover:border-ink"
                }`}
              >
                {s.label}
              </button>
            );
          })}
        </div>

        {/* Stage */}
        <div
          id="sector-stage"
          role="tabpanel"
          aria-labelledby={`sector-tab-${active.id}`}
          className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-12"
          data-testid="sector-stage"
        >
          <motion.div
            key={active.id}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7"
          >
            <ShowcaseVideo item={item} featured />
          </motion.div>

          {/* Sector detail panel — problem, governance moment, roles, evidence */}
          <div className="flex flex-col gap-6 lg:col-span-5">
            {/* The reader's problem */}
            <div className="border border-ink/15 p-6 md:p-7" data-testid="sector-problem">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink/45">
                The problem holder's decision
              </p>
              <p className="mt-4 font-serif text-lg leading-snug text-ink md:text-xl">
                {active.problem}
              </p>
            </div>

            {/* Governance moment — the decisive freeze / repair / re-check */}
            <div className="border-l-2 border-teal bg-ink p-6 text-stone md:p-7" data-testid="sector-governance-moment">
              <div className="flex items-center gap-2">
                <AlertOctagon className="h-4 w-4 text-teal" strokeWidth={1.75} />
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone/50">
                  Governance moment
                </p>
              </div>
              <p className="mt-4 font-sans text-sm leading-relaxed text-stone/85">
                {active.governanceMoment}
              </p>
            </div>

            {/* Principal stakeholder roles */}
            <div className="border border-ink/15 p-6 md:p-7">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-teal" strokeWidth={1.75} />
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink/45">
                  Principal stakeholder roles
                </p>
              </div>
              <ul className="mt-4 flex flex-wrap gap-2" data-testid="sector-stakeholders">
                {active.stakeholders.map((st) => (
                  <li key={st} className="border border-ink/20 px-3 py-1.5 font-sans text-sm text-ink/75">
                    {st}
                  </li>
                ))}
              </ul>
              {active.evidenceUrl && (
                <Link
                  to={active.evidenceUrl}
                  data-testid={`sector-evidence-${active.id}`}
                  className="group mt-6 inline-flex items-center gap-1.5 font-sans text-sm text-ink/75 transition-colors hover:text-teal"
                >
                  Technical evidence
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.75} />
                </Link>
              )}
            </div>
          </div>
        </div>

        <p className="mt-10 max-w-2xl font-mono text-[11px] leading-relaxed text-ink/45">
          No fabricated footage, transaction data or signed receipts are shown; the
          marketing site does not call the KATLAS signing node.
        </p>
      </div>
    </section>
  );
}
